/**
 * Digital-mode result actions: Download + native Share.
 *
 * Renders nothing in kiosk mode. In digital mode, attaches two buttons
 * to the Result page's action row:
 *
 *   Download — pulls the strip image (or other primary output) and
 *              triggers a single browser save. Replaces the kiosk's
 *              "auto-write-to-USB-folder on every capture" behavior;
 *              the consumer chooses when to save.
 *
 *   Share    — Web Share API. On mobile, includes the file in the
 *              share payload when `navigator.canShare({ files })`
 *              returns true; the OS share sheet then offers WhatsApp,
 *              Photos, etc. On desktop (or any browser that lacks
 *              file-share support), shares the public gallery URL only
 *              — the recipient opens the URL to see/download the photos.
 *
 * The full studio implementation in `dreamboothstudio.com/gallery/[sid]`
 * uses `useDirectShare` + `buildShareFiles` to batch-presign multiple
 * outputs (strip + GIF + video) and share them as one payload. Porting
 * that here is a follow-up — see docs/digital-mode-plan.md §2.3.1.
 * v1 ships single-strip share, which covers the dominant case.
 */

import React, { useState } from 'react'
import { FaShareAlt, FaDownload, FaExternalLinkAlt } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { isDigitalMode } from '@renderer/lib/runtime'
import { Button } from '@renderer/components'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'
import { useSessionStateStore } from '@renderer/stores/sessionState'

interface DownloadOutput {
  url: string
  filename: string
}

/** Best-effort file extension from the URL (ignoring query) or a MIME hint. */
function guessExtension(url: string): string {
  const clean = url.split('?')[0].toLowerCase()
  if (clean.endsWith('.png')) return '.png'
  if (clean.endsWith('.gif')) return '.gif'
  if (clean.endsWith('.mp4')) return '.mp4'
  if (clean.endsWith('.webm')) return '.webm'
  return '.jpg'
}

/**
 * Enumerate every downloadable output for the session — each individual photo,
 * the composed strip, and any GIF / video — mirroring the studio gallery's
 * "download all" so the consumer gets the whole set, not just the strip.
 *
 * `capturedItems` mixes raw photos with the strip/gif/video the pipeline appends
 * (see CapturedItem docs), so we classify each item and skip non-photo entries
 * when counting `photo-N`, then add strip/gif/video once from their own sources.
 */
function collectSessionOutputs(sessionId: string, stripUrl: string): DownloadOutput[] {
  const { gallery } = useSessionStateStore.getState()
  const items = gallery.capturedItems || []
  const outputs: DownloadOutput[] = []

  let photoNumber = 0
  for (const item of items) {
    const isStripItem = Boolean(item.id?.includes('dreambooth-strip') || item.stripUrl)
    const isMediaItem = Boolean(item.videoUrl || item.videoRemoteUrl || item.gifUrl)
    if (isStripItem || isMediaItem) continue
    const photo = item.aiProcessedPhotoUrl || item.photoUrl
    if (!photo) continue
    photoNumber += 1
    outputs.push({
      url: photo,
      filename: `dreambooth-${sessionId}-photo-${photoNumber}${guessExtension(photo)}`
    })
  }

  const strip = stripUrl || gallery.stripUrl || ''
  if (strip) {
    outputs.push({ url: strip, filename: `dreambooth-${sessionId}-strip${guessExtension(strip)}` })
  }

  const gifItem = items.find((it) => it.gifUrl)
  if (gifItem?.gifUrl) {
    outputs.push({ url: gifItem.gifUrl, filename: `dreambooth-${sessionId}-animation.gif` })
  }

  const videoItem = [...items].reverse().find((it) => it.videoRemoteUrl || it.videoUrl)
  const video = videoItem?.videoRemoteUrl || videoItem?.videoUrl
  if (video) {
    outputs.push({ url: video, filename: `dreambooth-${sessionId}-video.mp4` })
  }

  return outputs
}

/** Fetch (unless already a blob URL) and trigger a single browser save. */
async function downloadOutput(out: DownloadOutput): Promise<void> {
  try {
    let href = out.url
    const isBlob = out.url.startsWith('blob:')
    if (!isBlob) {
      const res = await fetch(out.url)
      if (!res.ok) return
      href = URL.createObjectURL(await res.blob())
    }
    const a = document.createElement('a')
    a.href = href
    a.download = out.filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    if (!isBlob) setTimeout(() => URL.revokeObjectURL(href), 2000)
  } catch (err) {
    console.warn('[DigitalResultActions] download failed:', out.filename, err)
  }
}

const DOWNLOAD_STAGGER_MS = 350

interface DigitalResultActionsProps {
  /** In-memory blob URL or presigned S3 URL of the primary output (strip image). */
  stripUrl: string
  /** Session id — used to build the public gallery URL for sharing. */
  sessionId: string
  /** Optional class shared with the other buttons in the action row. */
  className?: string
}

function buildGalleryUrl(sessionId: string): string {
  // Same public gallery URL the result-page QR encodes:
  // `${VITE_GALLERY_URL}/{sessionId}` (default dreamboothstudio.com/gallery),
  // honoring any per-project `galleryPublicBaseUrl`. Used for both the "Open
  // gallery" button and the share link so they match the QR.
  return resolveGallerySessionUrlFromStore(sessionId)
}

async function fetchStripAsFile(stripUrl: string, sessionId: string): Promise<File | null> {
  try {
    const res = await fetch(stripUrl)
    if (!res.ok) return null
    const blob = await res.blob()
    const ext = blob.type === 'image/png' ? '.png' : blob.type === 'image/gif' ? '.gif' : '.jpg'
    return new File([blob], `dreambooth-${sessionId}${ext}`, {
      type: blob.type || 'image/jpeg'
    })
  } catch (err) {
    console.warn('[DigitalResultActions] fetch strip failed:', err)
    return null
  }
}

export const DigitalResultActions: React.FC<DigitalResultActionsProps> = ({
  stripUrl,
  sessionId,
  className
}) => {
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)
  const [sharing, setSharing] = useState(false)

  if (!isDigitalMode()) return null
  if (!stripUrl) return null

  const handleDownload = async (): Promise<void> => {
    if (downloading) return
    setDownloading(true)
    try {
      // Download the whole set — each individual photo, the strip, and any
      // gif/video — like the studio gallery's "download all", not just the strip.
      const outputs = collectSessionOutputs(sessionId, stripUrl)
      if (outputs.length === 0) return
      for (let i = 0; i < outputs.length; i++) {
        await downloadOutput(outputs[i])
        // Stagger the saves so the browser doesn't throttle/merge them.
        if (i < outputs.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_STAGGER_MS))
        }
      }
    } finally {
      setDownloading(false)
    }
  }

  const handleOpenGallery = (): void => {
    // Open the public gallery in a new tab so the consumer can view / save all
    // their photos. /g/{sessionId} redirects to the studio gallery.
    window.open(buildGalleryUrl(sessionId), '_blank', 'noopener,noreferrer')
  }

  const handleShare = async (): Promise<void> => {
    if (sharing) return
    setSharing(true)
    try {
      const url = buildGalleryUrl(sessionId)
      const title = t('result.share.title', 'My Dreambooth photos')
      const text = t('result.share.text', 'Check out the photos I took on Dreambooth!')

      // Try file-share first when the browser supports it.
      const canShare = typeof navigator.share === 'function'
      const canShareFiles = canShare && typeof navigator.canShare === 'function'

      if (canShare) {
        try {
          if (canShareFiles) {
            const file = await fetchStripAsFile(stripUrl, sessionId)
            if (file && navigator.canShare({ files: [file] })) {
              await navigator.share({ title, text, url, files: [file] })
              return
            }
          }
          await navigator.share({ title, text, url })
          return
        } catch (err) {
          // AbortError = user cancelled — silent; other errors fall through to clipboard.
          if (err instanceof Error && err.name === 'AbortError') return
          console.warn('[DigitalResultActions] navigator.share failed:', err)
        }
      }

      // Clipboard fallback (desktop browsers without Web Share API).
      try {
        await navigator.clipboard.writeText(url)
      } catch (err) {
        console.warn('[DigitalResultActions] clipboard fallback failed:', err)
      }
    } finally {
      setSharing(false)
    }
  }

  return (
    <>
      <Button
        text={
          <span className="flex items-center gap-1 whitespace-nowrap">
            <FaDownload />
            {downloading
              ? t('result.download.saving', 'Saving...')
              : t('result.download.label', 'Download')}
          </span>
        }
        onClick={handleDownload}
        variant="secondary"
        className={className}
        disabled={downloading}
      />
      <Button
        text={
          <span className="flex items-center gap-1 whitespace-nowrap">
            <FaShareAlt />
            {sharing
              ? t('result.share.sharing', 'Sharing...')
              : t('result.share.label', 'Share')}
          </span>
        }
        onClick={handleShare}
        variant="primary"
        className={className}
        disabled={sharing}
      />
      <Button
        text={
          <span className="flex items-center gap-1 whitespace-nowrap">
            <FaExternalLinkAlt />
            {t('result.openGallery.label', 'Open gallery')}
          </span>
        }
        onClick={handleOpenGallery}
        variant="secondary"
        className={className}
      />
    </>
  )
}

export default DigitalResultActions
